import { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorPage } from './error-page';
import { logger } from '@/lib/logger';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional — renders a fallback inside the dashboard layout (with sidebar) */
  withinLayout?: boolean;
  /** Custom title override */
  title?: string;
  /** Custom message override */
  message?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * React Error Boundary — catches JavaScript errors anywhere in its child
 * component tree and renders a fallback UI instead of crashing the whole app.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <YourComponent />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error('[ErrorBoundary] Caught error:', error.message, errorInfo.componentStack);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorPage
          status={500}
          title={this.props.title}
          message={
            this.props.message ||
            (import.meta.env.DEV
              ? this.state.error?.message || 'An unexpected error occurred.'
              : 'Something went wrong on this page. Please try again.')
          }
          standalone={!this.props.withinLayout}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}
